"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CameraControls = exports.AxisDir = void 0;
var realms_three_1 = __importDefault(require("realms-three"));
var realms_browser_utils_1 = require("realms-browser-utils");
var realms_utils_1 = require("realms-utils");
var realms_engine_1 = require("realms-engine");
var SHIFT = "ShiftLeft";
var CMD = "MetaLeft";
var CameraType;
(function (CameraType) {
    CameraType["PerspectiveCamera"] = "perspectiveCamera";
    CameraType["OrthographicCamera"] = "orthographicCamera";
})(CameraType || (CameraType = {}));
var AxisDir;
(function (AxisDir) {
    AxisDir["XPos"] = "xPos";
    AxisDir["XNeg"] = "xNeg";
    AxisDir["YPos"] = "yPos";
    AxisDir["YNeg"] = "yNeg";
    AxisDir["ZPos"] = "zPos";
    AxisDir["ZNeg"] = "zNeg";
})(AxisDir || (exports.AxisDir = AxisDir = {}));
var DEFAULT_CAMERA_TARGET = {
    x: 0,
    y: 0,
    z: 0,
};
var DEFAULT_CAMERA_DISTANCE = 20;
var DEFAULT_HORIZONTAL_ANGLE = Math.PI / 4;
var DEFAULT_VERTICAL_ANGLE = (2 * Math.PI) / 3;
/**
 * Controls to handle rotation, transformation, dollying
 */
var CameraControls = /** @class */ (function () {
    function CameraControls(camera, domElement, options) {
        var _this = this;
        this.camera = camera;
        this.domElement = domElement;
        this.eventManager = new realms_utils_1.EventManager();
        this.events = this.eventManager.asEventEmitter();
        this.previousState = "rotate";
        this.target = new realms_three_1.default.Vector3(DEFAULT_CAMERA_TARGET.x, DEFAULT_CAMERA_TARGET.y, DEFAULT_CAMERA_TARGET.z);
        /**
         * Distance from the target
         */
        this.targetDistance = DEFAULT_CAMERA_DISTANCE;
        /**
         * Angle to rotate horizontally in radians.
         * Range from 0 to PI
         */
        this.horizontalAngle = DEFAULT_HORIZONTAL_ANGLE;
        /**
         * Angle to rotate vertically in radians.
         * Range from 0 to 2 * PI
         */
        this.verticalAngle = DEFAULT_VERTICAL_ANGLE;
        // Sensitivity
        this.orbitSensitivity = 0.002;
        this.dollySensitivity = 0.05;
        this.translateSensitivity = 0.01;
        // Listens to keyboard events
        this.keyboardListener = new realms_browser_utils_1.KeyboardListener();
        this.deltaThreshold = 3;
        this.lastDeltaLength = null;
        /**
         * Disables the controls
         */
        this.disabled = false;
        /**
         * Listens to wheel events
         */
        this.wheelListener = function (e) {
            if (_this.disabled) {
                return;
            }
            var currentState = _this.getState();
            var currentDeltaLength = (0, realms_engine_1.vector2Length)({ x: e.deltaX, y: e.deltaY });
            // Ignore because of inertia
            if (currentState !== _this.previousState &&
                _this.lastDeltaLength &&
                currentDeltaLength - _this.lastDeltaLength < _this.deltaThreshold) {
                _this.lastDeltaLength = currentDeltaLength;
                return;
            }
            switch (currentState) {
                case "translate": {
                    // Move vertically
                    _this.target.sub(new realms_three_1.default.Vector3(0, _this.translateSensitivity * e.deltaY, 0));
                    // Move horizontally
                    var horizontalDisplacement = _this.target
                        .clone()
                        .sub(_this.camera.position)
                        .applyEuler(new realms_three_1.default.Euler(0, Math.PI / 2, 0))
                        .setY(0)
                        .normalize()
                        .multiplyScalar(e.deltaX * _this.translateSensitivity);
                    _this.target.sub(horizontalDisplacement);
                    break;
                }
                case "dolly": {
                    _this.setTargetDistance(_this.targetDistance + _this.dollySensitivity * e.deltaY);
                    break;
                }
                case "rotate": {
                    _this.setHorizontalAngle(_this.horizontalAngle + _this.orbitSensitivity * e.deltaX);
                    _this.setVerticalAngle(_this.verticalAngle - _this.orbitSensitivity * e.deltaY);
                    break;
                }
            }
            _this.eventManager.dispatch("onChange", _this);
            _this.lastDeltaLength = currentDeltaLength;
            _this.previousState = currentState;
        };
        this.update();
        if (options === null || options === void 0 ? void 0 : options.initialState) {
            this.setCameraState(options === null || options === void 0 ? void 0 : options.initialState);
        }
        if ((options === null || options === void 0 ? void 0 : options.disabled) !== undefined) {
            this.disabled = options.disabled;
        }
    }
    CameraControls.setCameraToState = function (camera, cameraState) {
        camera.position.copy(CameraControls.getCameraPosition(cameraState));
        camera.lookAt(new realms_three_1.default.Vector3(cameraState.target.x, cameraState.target.y, cameraState.target.z));
    };
    CameraControls.getCameraPosition = function (cameraState) {
        var y = cameraState.targetDistance *
            Math.sin(cameraState.verticalAngle - Math.PI / 2);
        var x = Math.abs(cameraState.targetDistance *
            Math.cos(cameraState.verticalAngle - Math.PI / 2));
        var displacement = new realms_three_1.default.Vector3(x, y, 0);
        displacement.applyEuler(new realms_three_1.default.Euler(0, cameraState.horizontalAngle, 0));
        // Set position
        return new realms_three_1.default.Vector3(cameraState.target.x, cameraState.target.y, cameraState.target.z).add(displacement);
    };
    /**
     * Subscribes to mouse + keyboard events
     */
    CameraControls.prototype.subscribe = function () {
        this.domElement.addEventListener("wheel", this.wheelListener);
        this.keyboardListener.subscribe();
    };
    /**
     * Unsubscribes to mouse + keyboard events
     */
    CameraControls.prototype.unsubscribe = function () {
        this.domElement.removeEventListener("wheel", this.wheelListener);
        this.keyboardListener.unsubscribe();
    };
    /**
     * Sets the camera state
     */
    CameraControls.prototype.setCameraState = function (cameraState) {
        if (cameraState.target) {
            this.target.set(cameraState.target.x, cameraState.target.y, cameraState.target.z);
        }
        if (cameraState.horizontalAngle) {
            this.horizontalAngle = cameraState.horizontalAngle;
        }
        if (cameraState.verticalAngle) {
            this.verticalAngle = cameraState.verticalAngle;
        }
        if (cameraState.targetDistance) {
            this.targetDistance = cameraState.targetDistance;
        }
        this.update();
    };
    /**
     * Zooms into the camera
     */
    CameraControls.prototype.zoom = function (amount) {
        this.setTargetDistance(this.targetDistance - amount);
        this.update();
        return this.targetDistance;
    };
    /**
     * Gets the camera state
     */
    CameraControls.prototype.getCurrentCameraState = function () {
        return {
            target: { x: this.target.x, y: this.target.y, z: this.target.z },
            horizontalAngle: this.horizontalAngle,
            verticalAngle: this.verticalAngle,
            targetDistance: this.targetDistance,
            type: this.camera instanceof realms_three_1.default.PerspectiveCamera
                ? CameraType.PerspectiveCamera
                : CameraType.OrthographicCamera,
        };
    };
    /**
     * Changes the camera
     */
    CameraControls.prototype.setCamera = function (camera) {
        this.camera = camera;
    };
    /**
     * Sets the camera to face a specific axis
     */
    CameraControls.prototype.setCameraAxisDir = function (axisDir) {
        switch (axisDir) {
            case AxisDir.XPos: {
                this.setHorizontalAngle(0);
                this.setVerticalAngle(Math.PI / 2);
                break;
            }
            case AxisDir.XNeg: {
                this.setHorizontalAngle(Math.PI);
                this.setVerticalAngle(Math.PI / 2);
                break;
            }
            case AxisDir.YPos: {
                this.setHorizontalAngle(0);
                this.setVerticalAngle(Math.PI);
                break;
            }
            case AxisDir.YNeg: {
                this.setHorizontalAngle(0);
                this.setVerticalAngle(0);
                break;
            }
            case AxisDir.ZPos: {
                this.setHorizontalAngle((3 * Math.PI) / 2);
                this.setVerticalAngle(Math.PI / 2);
                break;
            }
            case AxisDir.ZNeg: {
                this.setHorizontalAngle(Math.PI / 2);
                this.setVerticalAngle(Math.PI / 2);
                break;
            }
        }
        this.update();
        this.eventManager.dispatch("onChange", this);
    };
    /**
     * Updates the controls
     */
    CameraControls.prototype.update = function () {
        this.camera.position.copy(CameraControls.getCameraPosition(this.getCurrentCameraState()));
        this.camera.lookAt(this.target);
    };
    /**
     * Angle in radians
     */
    CameraControls.prototype.setHorizontalAngle = function (angle) {
        this.horizontalAngle = angle % (2 * Math.PI);
    };
    /**
     * Angle in radians
     */
    CameraControls.prototype.setVerticalAngle = function (angle) {
        this.verticalAngle = Math.max(Math.min(angle, Math.PI), 0);
    };
    CameraControls.prototype.setTargetDistance = function (distance) {
        this.targetDistance = Math.max(1, distance);
    };
    CameraControls.prototype.getState = function () {
        var pressedKeys = this.keyboardListener.getPressedKeys();
        if (pressedKeys.includes(SHIFT)) {
            return "translate";
        }
        if (pressedKeys.includes(CMD)) {
            return "dolly";
        }
        return "rotate";
    };
    return CameraControls;
}());
exports.CameraControls = CameraControls;
